import { Link } from "react-router-dom";
import { Check, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

const DOT = {
  done: "bg-foreground/70 text-background",
  active: "bg-primary text-primary-foreground ring-4 ring-primary/15",
  blocked: "bg-rose-500 text-white",
  upcoming: "bg-transparent ring-1 ring-border",
  na: "bg-transparent ring-1 ring-border/60",
};

/**
 * Løpet – the quote's lifecycle as one quiet line: Utkast → Sendt → Åpnet av kunde → Svar → Prosjekt.
 * Each step carries a deterministic detail (date, views, days left). Not a wizard; steps are informational, some link.
 */
export const QuoteFlow = ({ steps, onStep, className }) => (
  <ol className={cn("-mx-1 flex items-start overflow-x-auto px-1 pb-1 scrollbar-thin", className)} aria-label="Tilbudets løp" data-testid="quote-flow">
    {steps.map((s, i) => {
      const clickable = Boolean(s.link || s.action);
      const Inner = (
        <>
          <div className="flex w-full items-center">
            <span className={cn("relative z-10 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px]", DOT[s.state])} aria-hidden>
              {s.state === "done" && <Check className="h-3 w-3" strokeWidth={3} />}
              {s.state === "blocked" && <AlertCircle className="h-3 w-3" />}
              {s.state === "active" && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
            </span>
            {i < steps.length - 1 && <span className={cn("h-px flex-1", s.state === "done" ? "bg-foreground/30" : "bg-border")} aria-hidden />}
          </div>
          <div className="mt-2 min-w-0 pr-4">
            <div className={cn("truncate text-[12.5px] font-medium leading-4", s.state === "upcoming" || s.state === "na" ? "text-muted-foreground" : s.state === "blocked" ? "text-rose-700 dark:text-rose-300" : "text-foreground")}>{s.label}</div>
            <div className={cn("truncate text-[11.5px] leading-4 text-muted-foreground", s.state === "active" && "text-foreground/80")}>{s.detail}</div>
          </div>
        </>
      );
      const cls = cn("group block w-full rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", clickable && "cursor-pointer [&_.truncate]:group-hover:underline [&_.truncate]:underline-offset-[3px]");
      return (
        <li key={s.key} className="min-w-[150px] flex-1 shrink-0 sm:min-w-0" data-testid={`quote-flow-${s.key}`} data-step-state={s.state}>
          {s.link ? <Link to={s.link} className={cls}>{Inner}</Link> : clickable ? <button type="button" onClick={() => onStep?.(s)} className={cn(cls, "w-full")}>{Inner}</button> : <div className={cls}>{Inner}</div>}
        </li>
      );
    })}
  </ol>
);
